/**
 * "Level Stats" modal: shows the per-level play telemetry recorded by
 * `gameplay/LevelStats.ts` and offers one-click clipboard copy in either
 * CSV (spreadsheet-friendly) or plain-text (chat/issue-friendly) form.
 *
 * Read-only: the modal never mutates state besides clearing all stats
 * via the explicit "Clear all" button (with a confirm prompt).
 */

import * as LevelStats from '../../../gameplay/LevelStats.js';
import type { LevelData } from '../../../gameplay/LevelLoader.js';
import { createModalShell, styleButton } from './modalUtils.js';

export interface LevelStatsController { close(): void }

export interface LevelStatsOptions {
  /** Built-in level catalog so we can show display names alongside ids. */
  allLevels: LevelData[];
  onClose?: () => void;
}

export function openLevelStatsModal(opts: LevelStatsOptions): LevelStatsController {
  const shell = createModalShell({
    width: 'min(720px, 94vw)',
    maxHeight: '88vh',
    ...(opts.onClose ? { onClose: opts.onClose } : {}),
  });

  const nameById = new Map<string, string>();
  for (const l of opts.allLevels) nameById.set(l.id, l.name);

  const heading = document.createElement('div');
  heading.textContent = 'Level statistics';
  Object.assign(heading.style, { fontWeight: '600', fontSize: '15px' } as Partial<CSSStyleDeclaration>);
  shell.panel.appendChild(heading);

  const sub = document.createElement('div');
  sub.style.opacity = '0.7';
  sub.style.fontSize = '12px';
  sub.textContent = 'Per-level attempts, deaths, abandons, and timing aggregated across this device.';
  shell.panel.appendChild(sub);

  // Scrollable table area.
  const tableWrap = document.createElement('div');
  Object.assign(tableWrap.style, {
    flex: '1', overflow: 'auto',
    border: '1px solid #2a1d4a', borderRadius: '4px',
    background: '#120a22',
  } as Partial<CSSStyleDeclaration>);
  shell.panel.appendChild(tableWrap);

  const renderTable = (): void => {
    const records = LevelStats.listAll();
    tableWrap.innerHTML = '';
    if (records.length === 0) {
      const empty = document.createElement('div');
      empty.style.padding = '16px';
      empty.style.opacity = '0.7';
      empty.textContent = 'No statistics recorded yet. Play a level to start collecting data.';
      tableWrap.appendChild(empty);
      return;
    }
    const table = document.createElement('table');
    Object.assign(table.style, {
      width: '100%', borderCollapse: 'collapse',
      font: '12px ui-monospace, SFMono-Regular, Menlo, monospace',
    } as Partial<CSSStyleDeclaration>);
    const thead = document.createElement('thead');
    const cols = [
      'Level', 'Plays', 'Wins', 'Deaths', 'Aban.', 'Skips', 'Restarts',
      'Win%', 'Avg', 'AvgWin', 'AvgDeath', 'Best', 'FastDth', 'Longest',
    ];
    const trh = document.createElement('tr');
    for (const c of cols) {
      const th = document.createElement('th');
      th.textContent = c;
      Object.assign(th.style, {
        textAlign: 'left', padding: '6px 8px',
        position: 'sticky', top: '0',
        background: '#1b0f30', borderBottom: '1px solid #2a1d4a',
        fontWeight: '600',
      } as Partial<CSSStyleDeclaration>);
      trh.appendChild(th);
    }
    thead.appendChild(trh);
    table.appendChild(thead);

    const tbody = document.createElement('tbody');
    for (const r of records) {
      const tr = document.createElement('tr');
      const winRate = r.plays > 0 ? ((r.wins / r.plays) * 100).toFixed(0) + '%' : '—';
      const avgAttempt = LevelStats.fmt.frames(LevelStats.fmt.avg(r.totalTimeFrames, r.plays));
      const avgWin = LevelStats.fmt.frames(LevelStats.fmt.avg(r.timeToWinFramesSum, r.wins));
      const avgDth = LevelStats.fmt.frames(LevelStats.fmt.avg(r.timeToDeathFramesSum, r.deaths));
      const cells = [
        nameById.get(r.levelId) ? `${r.levelId} — ${nameById.get(r.levelId)!}` : r.levelId,
        String(r.plays),
        String(r.wins),
        String(r.deaths),
        String(r.abandons),
        String(r.skips),
        String(r.restarts ?? 0),
        winRate,
        avgAttempt,
        avgWin,
        avgDth,
        LevelStats.fmt.frames(r.bestWinFrames),
        LevelStats.fmt.frames(r.fastestDeathFrames),
        LevelStats.fmt.frames(r.longestAttemptFrames),
      ];
      for (const c of cells) {
        const td = document.createElement('td');
        td.textContent = c;
        Object.assign(td.style, {
          padding: '5px 8px',
          borderBottom: '1px solid #1f1438',
          whiteSpace: 'nowrap',
        } as Partial<CSSStyleDeclaration>);
        tr.appendChild(td);
      }
      tbody.appendChild(tr);
    }
    table.appendChild(tbody);
    tableWrap.appendChild(table);
  };
  renderTable();

  // Status line for copy feedback.
  const status = document.createElement('div');
  Object.assign(status.style, {
    fontSize: '12px', opacity: '0.7', minHeight: '16px',
  } as Partial<CSSStyleDeclaration>);
  shell.panel.appendChild(status);

  const flash = (msg: string): void => {
    status.textContent = msg;
    window.setTimeout(() => { if (status.textContent === msg) status.textContent = ''; }, 2200);
  };

  const doCopy = async (text: string, label: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      flash(`Copied ${label} to clipboard (${text.length} chars).`);
    } catch {
      // Fallback: stuff into a textarea so the user can copy manually.
      const ta = document.createElement('textarea');
      ta.value = text;
      Object.assign(ta.style, {
        width: '100%', height: '160px', marginTop: '6px',
        background: '#0e0820', color: '#eee',
        border: '1px solid #444', borderRadius: '4px', padding: '6px',
        font: '12px ui-monospace, SFMono-Regular, Menlo, monospace',
      } as Partial<CSSStyleDeclaration>);
      shell.panel.insertBefore(ta, status);
      ta.focus(); ta.select();
      flash(`Clipboard blocked — ${label} shown in textarea, copy manually.`);
    }
  };

  // Buttons row.
  const buttons = document.createElement('div');
  Object.assign(buttons.style, {
    display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap',
  } as Partial<CSSStyleDeclaration>);

  const copyCsv = document.createElement('button');
  copyCsv.textContent = 'Copy as CSV';
  styleButton(copyCsv, true);
  copyCsv.addEventListener('click', () => {
    void doCopy(LevelStats.toCsv(LevelStats.listAll()), 'CSV');
  });
  buttons.appendChild(copyCsv);

  const copyText = document.createElement('button');
  copyText.textContent = 'Copy as text';
  styleButton(copyText);
  copyText.addEventListener('click', () => {
    void doCopy(LevelStats.toText(LevelStats.listAll()), 'text');
  });
  buttons.appendChild(copyText);

  const copyJson = document.createElement('button');
  copyJson.textContent = 'Copy as JSON';
  styleButton(copyJson);
  copyJson.addEventListener('click', () => {
    void doCopy(JSON.stringify(LevelStats.listAll(), null, 2), 'JSON');
  });
  buttons.appendChild(copyJson);

  const clearBtn = document.createElement('button');
  clearBtn.textContent = 'Clear all';
  styleButton(clearBtn);
  clearBtn.style.borderColor = '#aa3344';
  clearBtn.addEventListener('click', () => {
    const ok = window.confirm('Erase all recorded level statistics on this device?');
    if (!ok) return;
    LevelStats.clearAll();
    renderTable();
    flash('All statistics cleared.');
  });
  buttons.appendChild(clearBtn);

  const closeBtn = document.createElement('button');
  closeBtn.textContent = 'Close';
  styleButton(closeBtn);
  closeBtn.addEventListener('click', shell.close);
  buttons.appendChild(closeBtn);

  shell.panel.appendChild(buttons);

  document.body.appendChild(shell.overlay);
  return { close: shell.close };
}
